import { useMemo, useState, type ReactNode } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { ACADEMY_MANIFEST, type AcademyModuleMeta } from '../../content/academy/manifest';
import { ProgressBar } from './progress-bar';

type AcademyType = 'trading' | 'crypto';

interface Props {
  type: AcademyType;
  activeSlug: string | null;
  isRead: (slug: string) => boolean;
  onSelect: (slug: string) => void;
  onTypeChange: (type: AcademyType) => void;
}

interface Category {
  name: string;
  items: AcademyModuleMeta[];
}

function groupByCategory(type: AcademyType): Category[] {
  const cats: Category[] = [];
  for (const m of ACADEMY_MANIFEST.filter((x) => x.type === type)) {
    let cat = cats.find((c) => c.name === m.category);
    if (!cat) {
      cat = { name: m.category, items: [] };
      cats.push(cat);
    }
    cat.items.push(m);
  }
  for (const c of cats) c.items.sort((a, b) => a.order - b.order);
  return cats;
}

export function ModuleList({ type, activeSlug, isRead, onSelect, onTypeChange }: Props): ReactNode {
  const categories = useMemo(() => groupByCategory(type), [type]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const allItems = useMemo(() => categories.flatMap((c) => c.items), [categories]);
  const doneCount = allItems.filter((m) => isRead(m.slug)).length;
  const toggle = (name: string): void => setOpen((p) => ({ ...p, [name]: !(p[name] ?? true) }));
  const isOpen = (name: string): boolean => open[name] ?? true;

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex gap-1 rounded-xl border border-slate-700/40 p-1">
        {(['trading', 'crypto'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTypeChange(t)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-[13px] font-bold ${
              type === t ? 'bg-amber-500/15 text-amber-300' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {t === 'trading' ? 'Trading' : 'Crypto'}
          </button>
        ))}
      </div>
      <ProgressBar done={doneCount} total={allItems.length} />
      <nav className="flex-1 overflow-y-auto pr-1">
        {categories.map((cat) => (
          <div key={cat.name} className="mb-2">
            <button
              type="button"
              onClick={() => toggle(cat.name)}
              className="flex w-full items-center gap-1 py-1.5 text-left text-[13px] font-semibold text-slate-300"
            >
              {isOpen(cat.name) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className="truncate">{cat.name}</span>
            </button>
            {isOpen(cat.name) && (
              <ul className="ml-2 border-l border-slate-700/40">
                {cat.items.map((m) => {
                  const active = m.slug === activeSlug;
                  return (
                    <li key={m.slug}>
                      <button
                        type="button"
                        onClick={() => onSelect(m.slug)}
                        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] ${
                          active
                            ? 'border-l-2 border-amber-500 text-amber-200'
                            : 'text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        {isRead(m.slug) ? (
                          <Check size={13} className="shrink-0 text-emerald-400" />
                        ) : (
                          <span className="inline-block w-[13px] shrink-0" />
                        )}
                        <span className="truncate">{m.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}
